<template>
  <div class="home-page-container">
    <div class="home-page">
      <el-card class="user-profile-card" shadow="lg">
        <template #header>
          <div class="card-header">
            <el-icon :size="24" style="margin-right: 10px; color: #409EFF;"><UserFilled /></el-icon>
            <span>我的個人資料</span>
            <!-- <el-button type="primary" link icon="EditPen" size="small">編輯</el-button> -->
          </div>
        </template>

        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="6" animated />
        </div>

        <div v-else-if="error" class="error-message-container">
          <el-result
            icon="error"
            title="獲取失敗"
            :sub-title="error || '無法加載用戶數據，請稍後再試。'"
          >
            <template #extra>
              <el-button type="primary" @click="retryFetch" v-if="typeof retryFetch === 'function'">重試</el-button>
            </template>
          </el-result>
        </div>

        <div v-else-if="user" class="profile-details">
          <el-descriptions :column="isMobile ? 1 : 2" border :label-style="labelStyle" :content-style="contentStyle">
            <el-descriptions-item>
              <template #label>
                <div class="cell-item">
                  <el-icon><User /></el-icon>用戶暱稱
                </div>
              </template>
              {{ user.nickname || '未提供' }}
            </el-descriptions-item>

            <el-descriptions-item>
              <template #label>
                <div class="cell-item">
                  <el-icon><Avatar /></el-icon>真實姓名
                </div>
              </template>
              {{ user.real_name || '未提供' }}
            </el-descriptions-item>

            <el-descriptions-item>
              <template #label>
                <div class="cell-item">
                  <el-icon><Message /></el-icon>電子郵箱
                </div>
              </template>
              {{ user.email || '未提供' }}
            </el-descriptions-item>

            <el-descriptions-item>
              <template #label>
                <div class="cell-item">
                  <el-icon><Iphone /></el-icon>手機號碼
                </div>
              </template>
              {{ user.phone_number || '未提供' }}
            </el-descriptions-item>

            <el-descriptions-item :span="2">
              <template #label>
                <div class="cell-item">
                  <el-icon><CreditCard /></el-icon>身份證號碼
                </div>
              </template>
              {{ user.id_card_number || '未提供' }}
            </el-descriptions-item>

            <el-descriptions-item :span="2">
              <template #label>
                <div class="cell-item">
                  <el-icon><Timer /></el-icon>註冊時間
                </div>
              </template>
              {{ formatDateTime(user.created_at) }}
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <div v-else class="login-prompt-container">
           <el-empty :image-size="150" description=" ">
            <template #image>
                <img src="/path-to-your-login-illustration.svg" alt="請登入" style="width: 150px; height: 150px;"/>
                 <!-- 或者使用 Element Plus 提供的圖標 -->
                <!-- <el-icon :size="100" color="#C0C4CC"><Lock /></el-icon> -->
            </template>
            <p class="login-prompt-text">{{ authError ? '獲取用戶信息失敗或您尚未登入。' : '請先登入以查看您的個人資料。' }}</p>
            <el-button type="primary" round size="large" @click="goToLogin" style="margin-top: 20px;">
              {{ authError ? '嘗試登入' : '前往登入' }}
            </el-button>
          </el-empty>
        </div>
      </el-card>
      <ShippingAddress v-if="user && !loading && !error" style="margin-top: 30px;" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { User, Avatar, Message, Iphone, CreditCard, Timer, UserFilled, Lock, EditPen } from '@element-plus/icons-vue'; // 增加了 UserFilled, Lock, EditPen
import ShippingAddress from './component/ShoppingAddress.vue';

const store = useStore();
const router = useRouter();

const user = computed(() => store.state.auth.userInfo);
const loading = computed(() => store.state.auth.loading);
const error = computed(() => store.state.auth.error); // 假設這是獲取用戶數據的錯誤
const authError = computed(() => store.state.auth.authError); // 假設這是專門的認證錯誤

const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value < 768);

const updateWidth = () => {
  windowWidth.value = window.innerWidth;
};

// 假設Vuex store中可能有一個重試action
const retryFetch = () => {
  if (store.hasAction('auth/fetchCurrentUser')) { // 檢查action是否存在
    store.dispatch('auth/fetchCurrentUser');
  } else {
    console.warn("Vuex action 'auth/fetchCurrentUser' not found for retry.");
  }
};


onMounted(() => {
  window.addEventListener('resize', updateWidth);
  // 如果用戶信息為空且不在加載中，嘗試獲取一次
  if (!user.value && !loading.value && store.hasAction('auth/fetchCurrentUser')) {
      store.dispatch('auth/fetchCurrentUser');
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWidth);
});

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  try {
    return new Date(dateTimeString).toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }).replace(/\//g, '-'); // 將 / 替換為 -
  } catch {
    return dateTimeString;
  }
};

const goToLogin = () => {
  router.push('/auth');
};

// el-descriptions 的 label 和 content 樣式
const labelStyle = {
  fontWeight: '500',
  color: '#303133',
  minWidth: '100px', // 給標籤一個最小寬度
  backgroundColor: '#fafafa', // 淺色背景
};
const contentStyle = {
  color: '#606266',
  wordBreak: 'break-all',
};
</script>

<style scoped>
:root {
  --el-color-primary-light-9: #f4f8ff; /* Element Plus 淺藍色 */
  --el-text-color-regular: #303133;
  --el-text-color-secondary: #606266;
  --el-text-color-placeholder: #909399;
  --el-border-color-lighter: #e4e7ed;
  --el-fill-color-lighter: #fafafa;
}

.home-page-container {
  min-height: calc(100vh - 0px); /* 減去可能的 header 高度 */
  padding: 30px 20px; /* 增加上下 padding */
  display: flex;
  justify-content: center;
  align-items: flex-start; /* 從頂部開始對齊 */
}

.home-page {
  width: 100%;
  max-width: 1400px; /* 稍微增加最大寬度 */
}

.user-profile-card {
  border-radius: 12px; /* 更大的圓角 */
  border: none; /* 移除默認邊框，依賴陰影 */
  overflow: hidden; /* 確保內容不溢出圓角 */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08); /* 更柔和、現代的陰影 */
}

.card-header {
  display: flex;
  align-items: center;
  font-size: 1.3rem; /* 調整標題字體大小 */
  font-weight: 600; /* 加粗 */
  color: var(--el-text-color-regular);
  padding: 10px 0; /* 調整頭部內邊距 */
}
.card-header .el-button--primary.is-link {
    font-size: 0.9rem;
    margin-left: auto; /* 讓編輯按鈕靠右 */
}

.loading-container {
  padding: 40px 20px;
}

.error-message-container {
  padding: 30px 0;
  text-align: center;
}
.error-message-container .el-result {
    padding: 0; /* 移除 result 默認 padding */
}

.profile-details {
  padding: 10px 0px 20px 0px; /* 調整內邊距 */
}

/* el-descriptions 的整體樣式微調 */
.el-descriptions {
  font-size: 14px;
}

/* 覆蓋 el-descriptions-item 的標籤和內容樣式，如果 props 無法完全滿足 */
:deep(.el-descriptions__label.el-descriptions__cell.is-bordered-label) {
  background-color: var(--el-fill-color-lighter) !important;
  color: var(--el-text-color-secondary) !important;
  font-weight: 500 !important;
}
:deep(.el-descriptions__content.el-descriptions__cell.is-bordered-content) {
  color: var(--el-text-color-regular) !important;
}


.cell-item {
  display: flex;
  align-items: center;
  gap: 8px; /* 使用 gap 替代 margin-right */
}
.cell-item .el-icon {
  color: var(--el-text-color-placeholder); /* 統一圖標顏色 */
}

.login-prompt-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 20px;
  text-align: center;
  min-height: 300px; /* 給一個最小高度 */
}

.login-prompt-text {
  font-size: 1.1rem;
  color: var(--el-text-color-secondary);
  margin-top: 15px;
  margin-bottom: 25px;
  line-height: 1.6;
}

/* 響應式調整 */
@media (max-width: 768px) {
  .home-page-container {
    padding: 20px 15px;
  }
  .card-header {
    font-size: 1.15rem;
  }
  .el-descriptions {
    font-size: 13px;
  }
  :deep(.el-descriptions__label.el-descriptions__cell.is-bordered-label) {
      min-width: 80px !important;
  }
}
</style>